import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { buildAlertWithLocalizedMessages } from '../../actions'
import { getLocaleText } from '../../actions/datagrid'
import { getIntegrations } from '../../actions/organization/activeintegrations'
import { calendarPatch, calendarSetStatus, workhoursQuery } from '../../api'
import { Button } from '../../common/Button.tsx'
import Chip from '../../common/chip/Chip.jsx'
import {
	contextMenu,
	defaultColDef,
	getRowId,
	gridSettings,
	multiRow,
	sideBar,
	statusBarSelected,
} from '../../common/grid'
import { LoadingContainer } from '../../common/loaders/loadingcontainer'
import { PdfReport } from '../../common/pdfreport'
import Switch from '../../common/Switch.jsx'
import { dateFormatter, dateGetter, localDate } from '../../common/utils'
import { activeIntegrationsSelector, authSelector } from '../../reducers'
import { absenceNS, translatedCausecode } from '../personalprofile/newabscencemodal/newabscencemodal.constants'
import { WorkhoursModal } from './workhoursmodal'

const queryKey = { queryKey: ['workhours'] }

const openFilterModel = {
	filterType: 'set',
	values: ['OPEN'],
}

export function WorkhoursManagement() {
	const queryClient = useQueryClient()
	const { t, i18n } = useTranslation()
	const dispatch = useDispatch()

	const [loading, setLoading] = useState(true)
	useEffect(() => {
		async function fetchNamespaces() {
			await i18n.loadNamespaces([absenceNS])
			setLoading(false)
		}
		fetchNamespaces()
	}, [i18n.loadNamespaces])

	const auth = useSelector(authSelector)
	const activeIntegrations = useSelector(activeIntegrationsSelector)

	useEffect(() => {
		if (!activeIntegrations) {
			if (auth.companyBusinessId) {
				dispatch(getIntegrations(auth.companyBusinessId))
			} else {
				console.error('companyBusinessId null for', auth.companyNumber)
			}
		}
	}, [auth, dispatch, activeIntegrations])

	const [modalShow, setModalShow] = useState(false)
	const closeModal = (reloadData) => {
		if (reloadData) {
			queryClient.invalidateQueries(queryKey)
		}
		setModalShow(false)
	}

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const useFilter = (enable) => {
		const { api } = gridRef.current
		const statusColumn = 'status'

		enable ? api.setColumnFilterModel(statusColumn, openFilterModel) : api.setColumnFilterModel(statusColumn, null)

		api.onFilterChanged()
	}

	const useToggleShowAll = ({ target }) => useFilter(!target.checked)

	const useFirstDataRendered = (params) => {
		params.api.sizeColumnsToFit()
		useFilter(true)
	}

	const gridRef = useRef(null)

	const { data } = useQuery(workhoursQuery)
	const [selectedRows, setSelectedRows] = useState([])

	const mutateColumn = useMutation({
		mutationFn: (json) => calendarPatch(json.eventId, json),
		onError: ({ response }) => {
			dispatch(
				buildAlertWithLocalizedMessages(
					'ERROR',
					response.data.errorKeys,
					'Päällekkäinen kalenteritapahtuma',
					response.data.localizedMessages ? response.data.localizedMessages : null,
				),
			)
			queryClient.invalidateQueries(queryKey)
		},
	})

	const changeDate = (params) =>
		mutateColumn.mutate({
			eventId: params.data.id,
			[params.colDef.field]: localDate(params.newValue),
		})

	const mutateStatus = useMutation({
		mutationFn: ({ status, eventIds }) => calendarSetStatus(status, eventIds),
		onSuccess: () => {
			queryClient.invalidateQueries(queryKey)
		},
	})

	const approveSelected = () => {
		const eventIds = selectedRows.map((item) => item.id)
		mutateStatus.mutate({
			status: 'APPROVED',
			eventIds,
		})
	}

	const statusMap = {
		OPEN: { variant: 'warning', text: t('Open', 'Avoinna') },
		APPROVED: { variant: 'success', text: t('Approved', 'Hyväksytty') },
		REJECTED: { variant: 'error', text: t('Rejected', 'Hylätty') },
		DONE: { variant: 'info', text: t('Done', 'Valmis') },
	}

	const statusRenderer = (params) => {
		if (!params.value) {
			return ''
		}

		return <Chip variant={statusMap[params.value].variant}>{statusMap[params.value].text}</Chip>
	}

	const colDef = [
		{
			field: 'personNr',
			headerName: t('NumberAbbr', 'Nro'),
			filter: 'agNumberColumnFilter',
			//headerCheckboxSelection: true,
			//checkboxSelection: true,
		},
		{
			field: 'personName',
			headerName: t('Name', 'Nimi'),
		},
		{
			field: 'causeCode',
			headerName: t('AbsenceType', 'Poissaolon tyyppi'),
			valueFormatter: ({ value }) => value && translatedCausecode(value, t),
		},
		{
			field: 'startDate',
			headerName: t('Starting', 'Alkaa'),
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			onCellValueChanged: changeDate,
			editable: true,
			cellEditor: 'agDateCellEditor',
			singleClickEdit: true,
			//cellEditorPopup: true,
		},
		{
			field: 'endDate',
			headerName: t('Ending', 'Päättyy'),
			cellClass: 'dateType',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
			onCellValueChanged: changeDate,
			editable: true,
			cellEditor: 'agDateCellEditor',
			singleClickEdit: true,
			//cellEditorPopup: true,
		},
		{
			field: 'holidayAccrual',
			headerName: t('HolidayCredits', 'Lomasaldo'),
		},
		{
			field: 'status',
			filter: 'agSetColumnFilter',
			editable: true,
			cellEditor: 'agRichSelectCellEditor',
			cellEditorPopup: true,
			singleClickEdit: true,
			cellEditorParams: {
				values: ['OPEN', 'APPROVED', 'REJECTED', 'DONE'],
				cellRenderer: (params) => statusRenderer(params),
			},
			onCellValueChanged: (params) =>
				mutateColumn.mutate({
					eventId: params.data.id,
					[params.colDef.field]: params.newValue,
				}),
			cellRenderer: statusRenderer,
		},
		{
			field: 'icdCode',
		},
		{
			field: 'info',
		},
	]

	const getContextMenuItems = useCallback((params) => contextMenu(t, params), [t])

	return loading ? (
		<LoadingContainer />
	) : (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
			{modalShow && <WorkhoursModal closeModal={closeModal} />}

			<Row className="my-1">
				<Col sm="4">
					<h2 className="viewheading">{t('WorkhoursManagement', 'Työtuntien hallinta')}</h2>
					<Switch id="showAll" label={t('ShowAll', 'Näytä kaikki')} onChange={useToggleShowAll} />
				</Col>
				<Col sm="8" align="right">
					<Row className="g-2">
						<Col></Col>
						{activeIntegrations?.tokenSevera && (
							<Col md="auto" className="align-content-center">
								<Button variant="secondary" onClick={() => setModalShow(true)}>
									{t('ImportFromSevera', 'Tuo Severasta')}
								</Button>
							</Col>
						)}
						<Col md="auto">
							<Button
								testId="approve-selected"
								variant="primary"
								className="addbutton"
								disabled={selectedRows.length < 1}
								onClick={approveSelected}
							>
								{t('ApproveSelected', 'Hyväksy valitut')}
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>

			<Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
				<AgGridReact
					{...gridSettings}
					ref={gridRef}
					getContextMenuItems={getContextMenuItems}
					columnDefs={colDef}
					defaultColDef={{ ...defaultColDef, enableCellChangeFlash: true }}
					sideBar={sideBar}
					rowData={data}
					getMainMenuItems={(params) => params.defaultItems.slice(0, -1)} // remove resetColumns
					//getContextMenuItems={getContextMenuItems}
					statusBar={statusBarSelected}
					//onRowDoubleClicked={doubleClickAction}
					getRowId={getRowId}
					getLocaleText={(params) => getLocaleText(t, params)}
					rowSelection={multiRow}
					onSelectionChanged={({ api }) => setSelectedRows(api.getSelectedRows())}
					animateRows
					suppressCellFocus
					suppressColumnVirtualisation // https://github.com/ag-grid/ag-grid/issues/1692
					onFirstDataRendered={useFirstDataRendered}
					//initialState={loadViewState(viewStateKey) || initialState}
					//onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
					debug={import.meta.env.DEV}
				/>
			</Row>
		</>
	)
}
